// Shared QR identity helpers for orders. Kept in a non-component module so React
// Fast Refresh stays happy and both OrderQRCode and PrintLabel can reuse them.
import QRCode from 'qrcode'

// The QR identity payload generated and stored by the backend on order creation
// (orders.qr_payload). The scan-to-advance flow resolves the order by `token`
// (== orders.qr_token), so the QR MUST encode this object, never the human id.
export interface QrPayload {
  v?: number
  token?: string | null
  orderId?: string | null
  displayId?: string | number | null
  orderNumber?: string | null
}

// Mirror the server's encode options (order.controller.js getOrderQr) so the
// admin-rendered QR is identical to what agents/hub scan.
// `margin: 4` is the QR-spec-mandated quiet zone (anything less and scanners
// can fail at the edges of a thermal print).
export const QR_OPTIONS = { errorCorrectionLevel: 'Q' as const, margin: 4 }

// --- Print geometry (TSC TTP-244 = 203 dpi = 7.992 printer dots / mm) --------
// A thermal head is 1-bit (a dot is pure black OR white). The ONLY way to print
// a crisp QR is to author the bitmap so that ONE image pixel == ONE printer dot
// and EACH QR module == a whole number of dots - then the module edges land
// exactly on the dot grid (no anti-alias blur, no fractional resampling).
const DOTS_PER_MM = 203 / 25.4 // 7.992

// Pin the QR to version 5 so the module count is FIXED (37 data + 4-module quiet
// zone each side = 45) no matter which minimal payload we encode - the lib
// harmlessly upsizes the smaller {token} payload to v5. A fixed module count is
// what lets us guarantee an integer dots-per-module (and a fixed px↔mm mapping).
export const PRINT_QR_VERSION = 5
const PRINT_QR_MODULES = 37 + 4 * 2 // 45 (incl. quiet zone)

// px == printer dots: 45 modules × N dots/module. Display the <img> at the
// matching mm (px / DOTS_PER_MM) with `image-rendering: pixelated` so the print
// pipeline maps it 1:1. Keep px and mm locked via these helpers - a layout tweak
// that changes the displayed mm without changing px reintroduces the blur.
export const qrPrintPx = (dotsPerModule: number) => PRINT_QR_MODULES * dotsPerModule
export const qrPrintMm = (dotsPerModule: number) => qrPrintPx(dotsPerModule) / DOTS_PER_MM

/**
 * Render a PRINTED-label QR as a pixel-exact PNG data URL - the print-optimal
 * format for a 1-bit thermal head.
 *
 * Authored 1px = 1 printer dot at an INTEGER `dotsPerModule`, so the qrcode lib
 * draws each module as a whole-dot square (crisp), and - crucially - the <img>
 * is then displayed at qrPrintMm(dotsPerModule), i.e. exactly px ÷ 8 dots/mm, so
 * the browser/driver does NOT up- or down-scale it. With `image-rendering:
 * pixelated` on the <img>, nearest-neighbour at a 1:1 ratio is loss-less.
 *
 * This replaces the old SVG path: SVG is rasterised by the print pipeline with
 * anti-aliasing ON (greyscale edges → dithered → fuzzy modules on a 1-bit head),
 * and "crispEdges" only snaps to the rasteriser's own grid, never the printer's.
 *
 * MUST be used with the minimal token/id payload (see buildScanText) so it fits
 * v5-Q. `dotsPerModule`: 5 (→225px / 28.15mm) for vertical labels with room; 4
 * (→180px / 22.52mm) for the horizontal order label that shares the row w/ text.
 */
export const svgQrDataUrl = async (text: string, dotsPerModule = 5): Promise<string> => {
  if (!text) return ''
  try {
    const px = qrPrintPx(dotsPerModule)
    const canvas = document.createElement('canvas')
    canvas.width = px
    canvas.height = px
    const ctx = canvas.getContext('2d')
    if (ctx) ctx.imageSmoothingEnabled = false
    await QRCode.toCanvas(canvas, text, {
      errorCorrectionLevel: QR_OPTIONS.errorCorrectionLevel,
      margin: QR_OPTIONS.margin,
      version: PRINT_QR_VERSION,
      width: px,
      color: { dark: '#000000', light: '#FFFFFF' },
    })
    return canvas.toDataURL('image/png')
  } catch {
    return ''
  }
}

/**
 * Minimal text to encode in a PRINTED order QR - just the orderId (a UUID).
 * The backend scan resolver looks up the order by orders.id / orders.qr_token
 * and the agent pickup scanner reads `.orderId`, so nothing else is needed.
 * Encoding the full identity object pushed the QR to ~v13 (69 modules → ~2.5
 * dots/module at 24mm/203DPI = UNSCANNABLE on thermal). Token/id-only → ~v4-5
 * (big, scannable modules). Falls back to the token when no orderId is known.
 */
export const buildScanText = (props: {
  qrPayload?: QrPayload | null
  qrToken?: string | null
  orderId?: string | null
}): string | null => {
  const orderId = props.orderId ?? props.qrPayload?.orderId ?? null
  if (orderId) return JSON.stringify({ orderId: String(orderId) })
  const token = props.qrToken ?? props.qrPayload?.token ?? null
  if (token) return JSON.stringify({ token: String(token) })
  return null
}

/**
 * Resolve the exact text to encode. Prefer the stored qr_payload; otherwise
 * reconstruct it from qr_token + ids (mirrors the server fallback). Returns null
 * when there is nothing scannable to render.
 */
export const buildQrText = (props: {
  qrPayload?: QrPayload | null
  qrToken?: string | null
  orderId?: string | null
  displayId?: string | number | null
  orderNumber?: string | null
}): string | null => {
  const { qrPayload, qrToken, orderId, displayId, orderNumber } = props
  if (qrPayload && qrPayload.token) {
    return JSON.stringify(qrPayload)
  }
  if (qrToken) {
    return JSON.stringify({
      v: 1,
      token: qrToken,
      orderId: orderId ?? null,
      displayId: displayId ?? null,
      orderNumber: orderNumber ?? null,
    })
  }
  return null
}
